import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import BorrowerLayout from '@/layouts/BorrowerLayout'

interface PlanDetails {
  name: string
  price: number
  annual_price?: number
  loan_limit: number
  features: string[]
}

interface Props {
  plan: string
  subscription_plan: string
  subscription_status: string | null
  active_loan_count: number
  loan_limit: number
  plans: Record<string, PlanDetails>
  redirect_url?: string
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

const PLAN_ORDER = ['free', 'pro', 'portfolio'] as const

const TIER_RANK: Record<string, number> = { free: 0, pro: 1, portfolio: 2 }

export default function Show({
  plan,
  subscription_plan,
  subscription_status,
  active_loan_count,
  loan_limit,
  plans,
  redirect_url,
}: Props) {
  const [interval, setInterval] = useState<'monthly' | 'annual'>('monthly')

  useEffect(() => {
    if (redirect_url) {
      window.location.href = redirect_url
    }
  }, [redirect_url])

  const isPaid = plan === 'pro' || plan === 'portfolio'
  const usage = loan_limit ? Math.min((active_loan_count / loan_limit) * 100, 100) : 0
  const usageColor = usage >= 100 ? 'bg-red-500' : usage >= 80 ? 'bg-amber-500' : 'bg-emerald-500'

  function handleSubscribe(targetPlan: string) {
    router.post('/my-loans/billing/subscribe', { plan: targetPlan, interval })
  }

  function handlePortal() {
    router.post('/my-loans/billing/portal')
  }

  const statusBadge = () => {
    if (subscription_status === 'active') return { label: 'Active', className: 'bg-emerald-100 text-emerald-800' }
    if (subscription_status === 'past_due') return { label: 'Past Due', className: 'bg-red-100 text-red-800' }
    if (subscription_status === 'canceled') return { label: 'Canceled', className: 'bg-gray-100 text-gray-600' }
    return null
  }

  const badge = isPaid ? statusBadge() : null

  const planLabel = plan === 'portfolio' ? 'Portfolio' : plan === 'pro' ? 'Pro' : 'Free'

  return (
    <BorrowerLayout>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-xl font-bold text-gray-900 mb-6">Billing</h1>

        {/* Current Plan */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Current Plan</p>
              <p className="text-lg font-bold text-gray-900">
                DebtLens {planLabel}
              </p>
            </div>
            {badge && (
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${badge.className}`}>
                {badge.label}
              </span>
            )}
          </div>

          {/* Usage */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600">Active Loans</span>
              <span className="text-sm font-medium text-gray-900">
                {active_loan_count} / {loan_limit}
              </span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${usageColor}`} style={{ width: `${usage}%` }} />
            </div>
            {usage >= 80 && usage < 100 && (
              <p className="text-xs text-amber-600 mt-1">Approaching your plan limit</p>
            )}
          </div>

          {isPaid && (
            <button
              onClick={handlePortal}
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            >
              Manage subscription
            </button>
          )}
        </div>

        {/* Interval Toggle */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
            <button
              onClick={() => setInterval('monthly')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                interval === 'monthly' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setInterval('annual')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                interval === 'annual' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Annual
              <span className="ml-1 text-xs text-emerald-500">Save 17%</span>
            </button>
          </div>
        </div>

        {/* Plan Comparison — 3 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {PLAN_ORDER.map((key) => {
            const planDetail = plans[key]
            if (!planDetail) return null

            const isCurrent = plan === key
            const isUpgrade = (TIER_RANK[key] ?? 0) > (TIER_RANK[plan] ?? 0)
            const isPortfolio = key === 'portfolio'
            const highlightBorder = key === 'pro' ? 'border-emerald-500' : isPortfolio ? 'border-indigo-500' : 'border-gray-200'

            return (
              <div
                key={key}
                className={`bg-white rounded-xl border-2 p-6 relative ${highlightBorder}`}
              >
                {isPortfolio && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs font-semibold px-3 py-0.5 rounded-full">
                    Best for portfolios
                  </span>
                )}

                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-base font-bold text-gray-900">{planDetail.name}</h3>
                  {isCurrent && (
                    <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                      Current
                    </span>
                  )}
                </div>

                <div className="mb-4">
                  {planDetail.price === 0 ? (
                    <p className="text-2xl font-bold text-gray-900">Free</p>
                  ) : (
                    <div>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(interval === 'annual' && planDetail.annual_price ? planDetail.annual_price / 12 : planDetail.price)}
                        <span className="text-sm font-normal text-gray-500">/mo</span>
                      </p>
                      {interval === 'annual' && planDetail.annual_price && (
                        <p className="text-xs text-emerald-600 font-medium mt-0.5">
                          {formatCurrency(planDetail.annual_price)}/yr — save {formatCurrency(planDetail.price * 12 - planDetail.annual_price)}/yr
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <ul className="space-y-2 mb-6">
                  {planDetail.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <svg className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>

                {isUpgrade && (
                  <button
                    onClick={() => handleSubscribe(key)}
                    className={`w-full py-2.5 rounded-lg text-sm font-semibold text-white transition-colors ${
                      isPortfolio
                        ? 'bg-indigo-600 hover:bg-indigo-700'
                        : 'bg-emerald-600 hover:bg-emerald-700'
                    }`}
                  >
                    Upgrade to {planDetail.name}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </BorrowerLayout>
  )
}
